import React, { useState } from 'react';
import { Sparkles, Download, FileImage, AlertTriangle } from 'lucide-react';
import AdBanner from './AdBanner';
import AdLeaderboardSmall from './AdLeaderboardSmall';
import AdLeaderboardBottom from './AdLeaderboardBottom';
import AdDesignsBanner from './AdDesignsBanner';
import AdDesignMid from './AdDesignMid';
import { useLanguage } from '../contexts/LanguageContext';

interface DesignGeneratorProps {
  type: 'tattoo' | 'nail';
}

const NAIL_STYLES = [
  { value: 'minimalist', label: 'Minimalist' },
  { value: 'elegant', label: 'Elegant' },
  { value: 'french', label: 'French Tips' },
  { value: 'ombre', label: 'Ombre' },
  { value: 'gel', label: 'Gel' },
  { value: 'acrylic', label: 'Acrylic' },
  { value: 'glitter', label: 'Glitter' },
  { value: 'matte', label: 'Matte' },
  { value: 'metallic', label: 'Metallic' },
  { value: 'marble', label: 'Marble' },
  { value: 'floral', label: 'Floral' },
  { value: 'geometric', label: 'Geometric' },
];

// Updated to only include supported tattoo styles
const TATTOO_STYLES = [
  { value: 'Realism', label: 'Realism' },
  { value: 'Traditional', label: 'Traditional' },
  { value: 'Neo-Traditional', label: 'Neo-Traditional' },
  { value: 'Japanese Irezumi', label: 'Japanese Irezumi' },
  { value: 'Black and Grey', label: 'Black and Grey' },
  { value: 'Watercolor', label: 'Watercolor' },
  { value: 'Minimalist', label: 'Minimalist' },
  { value: 'Geometric', label: 'Geometric' },
  { value: 'Tribal', label: 'Tribal' }
];

const TATTOO_COLORS = [
  { value: 'black and grey', label: 'Black & Grey' },
  { value: 'colorful', label: 'Full Color' },
  { value: 'black', label: 'Black Only' },
  { value: 'red and black', label: 'Red & Black' },
  { value: 'blue tones', label: 'Blue Tones' },
  { value: 'green tones', label: 'Green Tones' },
  { value: 'pastel colors', label: 'Pastel Colors' },
  { value: 'vibrant colors', label: 'Vibrant Colors' },
  { value: 'muted colors', label: 'Muted Colors' },
  { value: 'earth tones', label: 'Earth Tones' },
];

const BODY_PARTS = [
  { value: 'arm', label: 'Arm' },
  { value: 'forearm', label: 'Forearm' },
  { value: 'back', label: 'Back' },
  { value: 'chest', label: 'Chest' },
  { value: 'shoulder', label: 'Shoulder' },
  { value: 'leg', label: 'Leg' },
  { value: 'thigh', label: 'Thigh' },
  { value: 'calf', label: 'Calf' },
  { value: 'ankle', label: 'Ankle' },
  { value: 'wrist', label: 'Wrist' },
  { value: 'neck', label: 'Neck' },
  { value: 'hand', label: 'Hand' },
  { value: 'finger', label: 'Finger' },
  { value: 'ribcage', label: 'Ribcage' },
  { value: 'foot', label: 'Foot' },
  { value: 'hip', label: 'Hip' },
  { value: 'skin', label: 'Skin (Generic)' },
];

const DesignGenerator: React.FC<DesignGeneratorProps> = ({ type }) => {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationTime, setGenerationTime] = useState(0);
  const [generatedImages, setGeneratedImages] = useState<string[]>([]);
  const [selectedStyle, setSelectedStyle] = useState(type === 'nail' ? 'elegant' : 'Traditional');
  const [selectedColors, setSelectedColors] = useState('black and grey');
  const [selectedBodyPart, setSelectedBodyPart] = useState('forearm');
  const [error, setError] = useState<string | null>(null);
  const [downloadStatus, setDownloadStatus] = useState<{[key: string]: 'idle' | 'downloading' | 'success' | 'error'}>({});
  const { t, language } = useLanguage();

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    
    setIsGenerating(true);
    setError(null);
    setGenerationTime(0);
    
    // Start timing
    const startTime = Date.now();
    
    // Update generation time every second
    const timer = setInterval(() => {
      setGenerationTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://htnwxptpkltxmfxkurxu.supabase.co';
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imh0bnd4cHRwa2x0eG1meGt1cnh1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDY3ODEzNDIsImV4cCI6MjA2MjM1NzM0Mn0.pSN_qiULZlhZDIDhFKWnHiyt-HWgszfR836aTEuRJjI';
      
      if (!supabaseAnonKey) {
        throw new Error('Missing Supabase anonymous key. Please check your environment configuration.');
      }
      
      if (type === 'nail') {
        // For nail designs
        console.log("Calling nail edge function with prompt:", prompt, "and style:", selectedStyle);
        
        try {
          const response = await fetch(`${supabaseUrl}/functions/v1/replicate-nails`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseAnonKey}`
            },
            body: JSON.stringify({
              prompt,
              style: selectedStyle,
              negative_prompt: "blurry, low quality, distorted, disfigured, ugly, bad anatomy",
              num_outputs: 4
            })
          });

          if (!response.ok) {
            console.error("Response status:", response.status);
            const errorData = await response.text();
            console.error("Error data:", errorData);
            
            let errorMessage = `API request failed with status: ${response.status}`;
            
            try {
              const parsedError = JSON.parse(errorData);
              errorMessage = parsedError.error || errorMessage;
              
              if (parsedError.details) {
                console.error("Error details:", parsedError.details);
                errorMessage += ` - ${parsedError.details}`;
              }
            } catch (e) {
              // If the error isn't valid JSON, use the raw text
              errorMessage = errorData || errorMessage;
            }
            
            throw new Error(errorMessage);
          }

          const data = await response.json();
          console.log("Response data:", data);
          
          if (!data.output) {
            throw new Error(data.error || 'No output received from the API');
          }
          
          setGeneratedImages(data.output);
        } catch (error) {
          console.error("Error:", error);
          throw error;
        }
      } else {
        // For tattoo designs, use the edge function with longer timeout
        console.log("Calling tattoo edge function with prompt:", prompt, "style:", selectedStyle, "colors:", selectedColors, "body part:", selectedBodyPart);
        
        try {
          const response = await fetch(`${supabaseUrl}/functions/v1/replicate-tattoos`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseAnonKey}`
            },
            body: JSON.stringify({
              prompt,
              style: selectedStyle,
              colors: selectedColors,
              body_part: selectedBodyPart,
              negative_prompt: "blurry, low quality, distorted, disfigured, ugly, bad anatomy, strength",
              num_outputs: 4
            })
          });

          if (!response.ok) {
            console.error("Response status:", response.status);
            const errorData = await response.text();
            console.error("Error data:", errorData);
            
            let errorMessage = `API request failed with status: ${response.status}`;
            
            try {
              const parsedError = JSON.parse(errorData);
              errorMessage = parsedError.error || errorMessage;
              
              if (parsedError.details) {
                console.error("Error details:", parsedError.details);
                errorMessage += ` - ${parsedError.details}`;
              }
            } catch (e) {
              // If the error isn't valid JSON, use the raw text
              errorMessage = errorData || errorMessage;
            }
            
            throw new Error(errorMessage);
          }

          const data = await response.json();
          console.log("Response data:", data);
          
          if (!data.output) {
            throw new Error(data.error || 'No output received from the API');
          }
          
          setGeneratedImages(data.output);
        } catch (error) {
          console.error("Error:", error);
          throw error;
        }
      }
    } catch (error) {
      console.error('Error generating designs:', error);
      setError((error as Error).message);
    } finally {
      clearInterval(timer);
      setIsGenerating(false);
    }
  };

  // Get translations based on design type
  const typePrefix = type === 'tattoo' ? 'tattoo.generator' : 'nail.generator';
  const placeholder = type === 'tattoo' 
    ? t('tattoo.generator.placeholder')
    : t('nail.generator.placeholder');

  const handleDownload = async (imageUrl: string, index: number) => {
    const imageKey = `${imageUrl}-${index}`;
    
    try {
      // Set downloading status
      setDownloadStatus(prev => ({...prev, [imageKey]: 'downloading'}));
      
      // Fetch the image
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }
      
      // Convert to blob
      const imageBlob = await response.blob();
      
      // Create object URL
      const blobUrl = URL.createObjectURL(imageBlob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${type}-design-${index+1}.jpg`;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      
      // Set success status
      setDownloadStatus(prev => ({...prev, [imageKey]: 'success'}));
      
      // Reset status after 2 seconds
      setTimeout(() => {
        setDownloadStatus(prev => {
          const newStatus = {...prev};
          delete newStatus[imageKey];
          return newStatus;
        });
      }, 2000);
      
    } catch (error) {
      console.error('Download error:', error);
      setDownloadStatus(prev => ({...prev, [imageKey]: 'error'}));
      
      // Show error in UI
      setError(`Failed to download image: ${(error as Error).message}`);
      
      // Reset error status after 3 seconds
      setTimeout(() => {
        setDownloadStatus(prev => {
          const newStatus = {...prev};
          delete newStatus[imageKey];
          return newStatus;
        });
      }, 3000);
    }
  };

  const getDownloadButtonContent = (imageUrl: string, index: number) => {
    const imageKey = `${imageUrl}-${index}`;
    const status = downloadStatus[imageKey] || 'idle';
    
    switch (status) {
      case 'downloading':
        return <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />;
      case 'success':
        return <span className="text-green-500">✓</span>;
      case 'error':
        return <span className="text-red-500">×</span>;
      default:
        return <Download className="h-5 w-5" />;
    }
  };

  return (
    <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
      <div className="grid grid-cols-1 gap-8">
        {/* Input Section */}
        <div>
          <h2 className="text-2xl font-bold mb-6">
            {t(`${typePrefix}.title`)}
          </h2>
          
          <div className="mb-6">
            <label htmlFor="prompt" className="block text-sm font-medium text-gray-300 mb-2">
              {t(`${typePrefix}.describePrompt`)}
            </label>
            <textarea
              id="prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={placeholder}
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              rows={4}
            />
            {/* English language recommendation */}
            <div className="mt-2 text-sm p-2 rounded-md bg-purple-900/20 border border-purple-800/30 text-purple-300">
              <strong>Pro Tip:</strong> {t(`${typePrefix}.languageTip`)}
            </div>
          </div>
          
          {/* Style Selector - Different for tattoo and nail */}
          <div className="mb-6">
            <label htmlFor="style" className="block text-sm font-medium text-gray-300 mb-2">
              {t(`${typePrefix}.styleOptions`)}
            </label>
            <select
              id="style"
              value={selectedStyle}
              onChange={(e) => setSelectedStyle(e.target.value)}
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            >
              {type === 'nail' ? 
                NAIL_STYLES.map((style) => (
                  <option key={style.value} value={style.value}>
                    {style.label}
                  </option>
                )) : 
                TATTOO_STYLES.map((style) => (
                  <option key={style.value} value={style.value}>
                    {style.label}
                  </option>
                ))
              }
            </select>
          </div>
          
          {/* Additional options for tattoo designs */}
          {type === 'tattoo' && (
            <>
              <div className="mb-6">
                <label htmlFor="colors" className="block text-sm font-medium text-gray-300 mb-2">
                  {t(`${typePrefix}.colorOptions`)}
                </label>
                <select
                  id="colors"
                  value={selectedColors}
                  onChange={(e) => setSelectedColors(e.target.value)}
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                >
                  {TATTOO_COLORS.map((color) => (
                    <option key={color.value} value={color.value}>
                      {color.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="mb-6">
                <label htmlFor="bodyPart" className="block text-sm font-medium text-gray-300 mb-2">
                  {t(`${typePrefix}.bodyPartOptions`)}
                </label>
                <select
                  id="bodyPart"
                  value={selectedBodyPart}
                  onChange={(e) => setSelectedBodyPart(e.target.value)}
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                >
                  {BODY_PARTS.map((part) => (
                    <option key={part.value} value={part.value}>
                      {part.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
          
          {error && (
            <div className="mb-6 p-4 bg-red-900/30 border border-red-800 rounded-lg text-red-200">
              {error}
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <button
              onClick={handleGenerate}
              disabled={isGenerating || !prompt.trim()}
              className={`bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 px-8 rounded-full flex items-center justify-center gap-2 transition-all ${
                isGenerating || !prompt.trim() ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  {type === 'tattoo' 
                    ? `${t(`${typePrefix}.generating`)} (${generationTime}s)` 
                    : t(`${typePrefix}.generating`)}
                </>
              ) : (
                <>
                  <Sparkles className="h-5 w-5" />
                  {t(`${typePrefix}.generate`)}
                </>
              )}
            </button>
          </div>
          
          {/* Special note for tattoo generation */}
          {isGenerating && type === 'tattoo' && (
            <div className="mb-6 p-4 bg-amber-900/30 border border-amber-800 rounded-lg text-amber-200 flex gap-3 items-start">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Tattoo designs take longer to generate</p>
                <p className="text-sm">This process may take up to 2 minutes. Please be patient while our AI creates your custom design.</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Results Display - Always visible */}
        <div className="mt-6">
          <h3 className="text-xl font-bold mb-4">{t(`${typePrefix}.recentDesigns`)}</h3>
          
          {/* Small Leaderboard Ad Banner above designs */}
          <div className="mb-6">
            <AdLeaderboardSmall />
          </div>
          
          {/* Conditional content: Show designs or placeholder message */}
          {generatedImages.length > 0 ? (
            <>
              {/* First row of designs (2 images) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {generatedImages.slice(0, 2).map((img, index) => (
                  <div 
                    key={index} 
                    className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700 group hover:border-purple-500 transition-all"
                  >
                    <div className="aspect-square overflow-hidden">
                      <img 
                        src={img} 
                        alt={`Generated ${type} design ${index+1}`} 
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-3 flex justify-between items-center">
                      <span className="text-sm text-gray-400">{t(`${typePrefix}.design`)}{index+1}</span>
                      <button 
                        className="text-gray-400 hover:text-pink-500 transition-colors p-2 rounded-full hover:bg-gray-700"
                        aria-label={t('designCard.download')}
                        onClick={() => handleDownload(img, index)}
                        disabled={downloadStatus[`${img}-${index}`] === 'downloading'}
                      >
                        {getDownloadButtonContent(img, index)}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* 300x250 Ad Banner between rows */}
              <div className="my-6">
                <AdDesignMid className="max-w-4xl mx-auto" />
              </div>
              
              {/* Second row of designs (2 more images) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {generatedImages.slice(2, 4).map((img, index) => (
                  <div 
                    key={index + 2} 
                    className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700 group hover:border-purple-500 transition-all"
                  >
                    <div className="aspect-square overflow-hidden">
                      <img 
                        src={img} 
                        alt={`Generated ${type} design ${index+3}`} 
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-3 flex justify-between items-center">
                      <span className="text-sm text-gray-400">{t(`${typePrefix}.design`)}{index+3}</span>
                      <button 
                        className="text-gray-400 hover:text-pink-500 transition-colors p-2 rounded-full hover:bg-gray-700"
                        aria-label={t('designCard.download')}
                        onClick={() => handleDownload(img, index + 2)}
                        disabled={downloadStatus[`${img}-${index+2}`] === 'downloading'}
                      >
                        {getDownloadButtonContent(img, index + 2)}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            // Explanatory text when no designs have been generated
            <div className="bg-gray-800 rounded-lg p-8 border border-gray-700 text-center max-w-4xl mx-auto">
              <FileImage className="h-12 w-12 mx-auto mb-4 text-gray-600" />
              <h4 className="text-xl font-medium text-gray-300 mb-2">
                {type === 'tattoo' 
                  ? t('tattoo.noDesignsYet.title') || 'No designs generated yet' 
                  : t('nail.noDesignsYet.title') || 'No designs generated yet'}
              </h4>
              <p className="text-gray-400 max-w-md mx-auto">
                {type === 'tattoo'
                  ? t('tattoo.noDesignsYet.description') || 'Enter a description above and click "Generate Designs" to create your custom tattoo design.'
                  : t('nail.noDesignsYet.description') || 'Enter a description above and click "Generate Designs" to create your custom nail art design.'}
              </p>
            </div>
          )}
          
          {/* New Banner below recent designs */}
          <div className="mt-8 mb-6">
            <AdDesignsBanner className="max-w-4xl mx-auto" />
          </div>
          
          {/* Bottom leaderboard ad - always visible */}
          <div className="mt-6">
            <AdLeaderboardBottom className="max-w-4xl mx-auto" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignGenerator;